import React, { useEffect, useRef } from 'react';
import { Control, Controller } from 'react-hook-form';
import { Box, IconButton, Stack, Typography } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import WYSIWYGEditor, {
  EditorImages,
} from 'components/RichTextEditorField/WYSIWYGEditor';
import { hasMessageContent } from 'utils/triage';

// Mirrors the server's per-image cap and types (BL-TASK-1423), checked after
// the editor downscales; the 3 MB per-message total is left to the server,
// whose message is shown on refusal.
const TRIAGE_IMAGES: EditorImages = {
  maxBytes: 1024 * 1024,
  types: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
};

export const PRIMARY_BUTTON_SX = {
  backgroundColor: '#213B54',
  borderRadius: '8px',
  paddingX: '20px',
  height: '40px',
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  ':hover': { backgroundColor: '#16293B' },
};

export const ERROR_TEXT_SX = { fontSize: '13px', color: '#B42318' };

interface ITriageTakeoverProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  backLabel: string;
  onBack: () => void;
  /** Held while a send is in flight, so it cannot be abandoned mid-way. */
  backDisabled?: boolean;
  /** Sits opposite the title, e.g. the status control. */
  action?: React.ReactNode;
  /** Pinned below the scrolling body, e.g. the send or reply controls. */
  footer?: React.ReactNode;
}

// A triage being written or read owns the whole rail: a fixed header with the
// way back, a body that scrolls on its own, and an optional pinned footer so
// the send controls never scroll out of reach.
export const TriageTakeover: React.FC<ITriageTakeoverProps> = ({
  title,
  subtitle,
  backLabel,
  onBack,
  backDisabled,
  action,
  footer,
  children,
}) => {
  const headingRef = useRef<HTMLHeadingElement>(null);
  // The control that opened this view is gone, so keyboard and screen reader
  // focus would otherwise fall to the page body.
  useEffect(() => headingRef.current?.focus(), []);

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        alignItems="flex-start"
        gap={1}
        sx={{
          flex: 'none',
          padding: '14px 16px 14px 8px',
          borderBottom: '1px solid #EFEDEB',
        }}
      >
        <IconButton
          onClick={onBack}
          disabled={backDisabled}
          aria-label={backLabel}
          sx={{ color: '#213B54', marginTop: '-2px' }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: '20px' }} />
        </IconButton>
        <Stack sx={{ flex: 1, minWidth: 0, gap: '2px' }}>
          <Typography
            ref={headingRef}
            component="h2"
            tabIndex={-1}
            sx={{
              fontSize: '17px',
              fontWeight: 700,
              lineHeight: '24px',
              color: '#213B54',
              overflowWrap: 'anywhere',
              outline: 'none',
            }}
          >
            {title}
          </Typography>
          {!!subtitle && (
            <Typography
              sx={{ fontSize: '13px', lineHeight: '18px', color: '#7D818C' }}
            >
              {subtitle}
            </Typography>
          )}
        </Stack>
        {action}
      </Stack>

      <Stack
        sx={{
          flex: 1,
          minHeight: '120px',
          overflowY: 'auto',
          padding: '16px',
          gap: '16px',
        }}
      >
        {children}
      </Stack>

      {footer && (
        <Stack
          sx={{
            flex: 'none',
            // A long reply scrolls inside the footer rather than growing it
            // until the thread is gone and the send button is clipped.
            maxHeight: '65%',
            overflowY: 'auto',
            padding: '12px 16px 16px',
            gap: '12px',
            borderTop: '1px solid #EFEDEB',
            backgroundColor: '#FBFAF9',
          }}
        >
          {footer}
        </Stack>
      )}
    </Stack>
  );
};

export const FieldLabel: React.FC<{ required?: boolean }> = ({
  required,
  children,
}) => (
  <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#213B54' }}>
    {children}
    {required && (
      <Typography component="span" sx={{ color: '#B42318' }} aria-hidden>
        {' '}
        *
      </Typography>
    )}
  </Typography>
);

interface IMessageEditorProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  label: string;
  placeholder: string;
  minHeight: string;
  readOnly?: boolean;
  /** Grow to fill the space the parent leaves, as the create form does. */
  fill?: boolean;
  /** Shown under the editor when the body is empty on submit. */
  requiredMessage?: string;
  /** Reports an image still being processed, so Send can wait for it. */
  onImagesPending?: (pending: boolean) => void;
}

// The rich text editor under a plain label, rather than the shared field's
// floating label, which sat on the toolbar's border.
export const MessageEditor: React.FC<IMessageEditorProps> = ({
  control,
  name,
  label,
  placeholder,
  minHeight,
  readOnly,
  fill,
  requiredMessage,
  onImagesPending,
}) => (
  <Controller
    control={control}
    name={name}
    // The editor's empty value is "<p></p>", which a plain `required` rule
    // reads as filled in. A pasted image on its own is a message.
    rules={
      requiredMessage
        ? {
            validate: (value: string) =>
              hasMessageContent(value) ? true : requiredMessage,
          }
        : {}
    }
    render={({ field, fieldState }) => (
      <>
        <Box className={fill ? 'triage-editor-fill' : undefined}>
          <WYSIWYGEditor
            value={field.value}
            onChange={field.onChange}
            placeholder={placeholder}
            ariaLabel={label}
            readOnly={readOnly}
            images={TRIAGE_IMAGES}
            onImagesPending={onImagesPending}
            wrapperClassName="triage-editor"
            editorStyle={{
              minHeight,
              paddingLeft: '12px',
              paddingRight: '12px',
            }}
          />
        </Box>
        {!!fieldState.error && (
          <Typography sx={ERROR_TEXT_SX}>{fieldState.error.message}</Typography>
        )}
      </>
    )}
  />
);
